import { GRADE_PHASES } from '@/utils/gradePhases';

const own = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);
const validIds = (value) => Array.isArray(value) && value.length > 0
  && value.every((id) => typeof id === 'string' && id.trim() === id && id.length > 0)
  && new Set(value).size === value.length;

export function inspectPhaseAudience(audience) {
  if (audience?.scope !== 'phases') return { representation: 'not-phase', references: [] };
  const canonical = audience.phaseReferenceVersion === 1;
  const legacy = !own(audience, 'phaseReferenceVersion');
  const references = canonical ? audience.phaseIds : legacy ? audience.phases : null;
  const valid = (canonical || legacy) && validIds(references)
    && !(canonical ? own(audience, 'phases') : own(audience, 'phaseIds'));
  if (!valid) return { representation: 'invalid', references: [] };
  return { representation: canonical ? 'canonical' : 'legacy', references: [...references] };
}

export function normalizeCalendarAudience(audience, { allowLegacy = false } = {}) {
  const scope = audience?.scope || 'everyone';
  if (scope === 'phases') {
    const inspection = inspectPhaseAudience(audience);
    if (allowLegacy && inspection.representation === 'legacy') return { ...audience };
    if (inspection.representation !== 'canonical') {
      throw new Error('Choose one or more current school phases before saving.');
    }
    return {
      scope: 'phases',
      phaseReferenceVersion: 1,
      phaseIds: inspection.references,
      ...(validIds(audience.legacyPhaseKeys)
        ? { legacyPhaseKeys: [...audience.legacyPhaseKeys] }
        : {}),
    };
  }
  const fields = {
    grades: 'gradeIds',
    classes: 'classIds',
    committees: 'committeeIds',
  };
  if (scope === 'everyone') return { scope: 'everyone' };
  const field = fields[scope];
  if (!field || !validIds(audience?.[field])) {
    throw new Error('Choose at least one audience, or use Whole school.');
  }
  return { scope, [field]: [...audience[field]] };
}

export function audienceLabel(audience, phases = []) {
  if (!audience || audience.scope === 'everyone') return 'Whole school community';

  switch (audience.scope) {
    case 'phases': {
      const inspection = inspectPhaseAudience(audience);
      if (inspection.representation === 'legacy') {
        return inspection.references
          .map((phase) => GRADE_PHASES[phase]?.label || `Legacy: ${phase}`)
          .join(', ');
      }
      if (inspection.representation !== 'canonical') return 'Phase audience needs review';
      const names = new Map(phases.map((phase) => [phase.id, phase.name]));
      return inspection.references
        .map((phaseId) => names.get(phaseId) || `Unavailable: ${phaseId}`)
        .join(', ');
    }
    case 'grades':
      return audience.gradeIds?.length
        ? `${audience.gradeIds.length} ${audience.gradeIds.length === 1 ? 'grade' : 'grades'}`
        : 'Staff only';
    case 'classes':
      return audience.classIds?.length
        ? `${audience.classIds.length} ${audience.classIds.length === 1 ? 'class' : 'classes'}`
        : 'Staff only';
    case 'committees':
      return audience.committeeIds?.length
        ? `${audience.committeeIds.length} ${audience.committeeIds.length === 1 ? 'committee' : 'committees'}`
        : 'Staff only';
    default:
      return 'Audience needs review';
  }
}
